/**
 * The GPU half of ENHANCED EFFECTS' tracers — **two instanced meshes, two draw
 * calls, no texture**, however many rounds are in the sky and however many
 * sides are firing them.
 *
 * The arithmetic is `flight/tracer-bolts.ts`'s and its header carries the
 * measurement that motivated the whole thing; this file is the scene graph, the
 * instance matrices and the two materials.
 *
 * ## Why two meshes and not one
 *
 * They are the two halves a tracer actually has, and they behave oppositely
 * under the projection.
 *
 * The **tail** is a stretched billboard: a quad that contains the round's world
 * flight direction and is rolled about it to face the camera, `TRACER_TAIL_
 * LENGTH_M` long in *metres*. Its foreshortening is therefore the projection's
 * own, for free and exactly right — full length across your view, collapsing to
 * nothing down your line of sight.
 *
 * The **head** is the opposite: a camera-facing quad at a *constant apparent
 * size*, so it never foreshortens and never shrinks below a visible dot. That
 * is what keeps a round fired straight at or straight away from you on screen
 * when its tail has projected to nothing — which is the case that matters,
 * because it is every round of every burst fired at something ahead.
 *
 * One mesh cannot be both. Two is also *fewer* than the three the authentic
 * path uses (`combat-visuals.ts` runs one `InstancedMesh` per `BULLETS.BMP`
 * size bucket), so switching the toggle on costs **−1 draw call**, not more.
 *
 * ## Side tinting is per-instance, and that is why it is free
 *
 * A tracer's colour now depends on who fired it — the player's own rounds, his
 * flight's, and the enemy's each take a different rung of `BARON.PAL`'s fire
 * ramp. **This is an invented port enhancement**; the original draws one
 * projectile colour for everyone. `flight/tracer-tint.ts` owns the three rungs,
 * the derivation that sets each one's luminance, and the evidence for the
 * "invented" claim; this file only reads the result and writes it per instance.
 *
 * **How much of the picture that rung is worth is `tracer-materials.ts`'
 * decision**, and on 2026-08-25 it turned out to be the larger half of the
 * problem: the shaders were handing the per-instance tint 30.4 % of the head's
 * emitted luminance and 26.6 % of the tail's. That file carries the
 * rearrangement and its arithmetic; nothing about it changed the per-instance
 * plumbing below.
 *
 * The obvious alternative was a mesh per tier, and it is worse on every axis
 * this file already cares about. Draw calls: the per-instance route holds the
 * pool at **two**, unconditionally, because the tint rides an
 * `InstancedBufferAttribute` and one mesh can carry all three streams. A mesh
 * per tier is two *per tier that is currently shooting* — four in the duel this
 * pool was tuned against, six the moment a wingman opens up, and the header's
 * "43 against the authentic path's 44" would have become 47. Memory: each of
 * the six would need its own `MAX_TRACERS` instance capacity, since a tier's
 * share of the cap is not knowable in advance, so the pool's buffers triple to
 * hold the same 256 rounds. And the sort: three transparent additive meshes at
 * one `renderOrder` is exactly the kind of order-dependent tie this file spent
 * its `depthWrite: false` argument removing.
 *
 * Against that the per-instance cost is three floats per round per mesh per
 * frame and one extra vertex-attribute fetch in each shader, with no change to
 * the material count, the pass, or any of `tracer-materials.ts`' four flags.
 *
 * **Which rung a side gets is `flight/tracer-tint.ts`'s question and where in
 * the round it goes is `tracer-materials.ts`'s** — this file only looks the tint
 * up and writes it to the instance attribute. That three-way split is what the
 * 2026-08-25 legibility revision left behind; the shading turned out to matter
 * more to the answer than the palette did, and it now has somewhere to be
 * argued.
 *
 * **Measured on the swapchain**, in the same frozen frame the tints were
 * measured in, by the one comparison that is not between two runs: count draws
 * with the pool drawing, hide both meshes, count again. Across noon and night
 * and both cameras the pool costs **1.000 draw calls per queue submit**, and
 * this renderer submits **2.000 times per presented frame** (counted against
 * `requestAnimationFrame`, because a per-submit figure is otherwise out by
 * exactly that factor) — so **two draw calls per frame with all three streams in
 * the air**, which is the two meshes and nothing else.
 * `docs/engineering/enhanced-tracers.md` §6 carries the table.
 */

import * as THREE from "three/webgpu";
import { createTracerPool, MAX_TRACERS, type TracerBolt } from "../flight/tracer-bolts.ts";
import { TRACER_TIER_TINT } from "../flight/tracer-tint.ts";
import {
  TRACER_TINT_ATTRIBUTE,
  tracerHeadMaterial,
  tracerTailMaterial,
} from "./tracer-materials.ts";

/**
 * Last, above the propeller disc's `1` and the searchlight beams' `3`.
 *
 * The transparent pass draws after everything opaque whatever the numbers say,
 * so what this actually settles is the order *within* it: a tracer adds over
 * the disc rather than being sorted behind it, which is the 2026-08-21 "rounds
 * dimmed to invisibility under the disc" report answered structurally instead
 * of by brightness. `depthTest` still means an aeroplane in front of a round
 * hides it, so this buys ordering and not occlusion.
 */
export const TRACER_RENDER_ORDER = 4;

/**
 * One mesh's per-instance tint buffer.
 *
 * One per mesh rather than one object shared by both geometries: the two are
 * written in the same loop from the same bolt, so the duplication is three
 * float stores per round per frame — at the `MAX_TRACERS` cap, 768 stores, next
 * to nothing — and it costs nothing to reason about, whereas handing the same
 * `InstancedBufferAttribute` to two geometries asks a question about the
 * backend's per-attribute buffer cache that this lane has no measurement for.
 * The repo's rule after four broken flags is to not answer those from the
 * source.
 */
function instancedTint(): THREE.InstancedBufferAttribute {
  const attribute = new THREE.InstancedBufferAttribute(new Float32Array(MAX_TRACERS * 3), 3);
  attribute.setUsage(THREE.DynamicDrawUsage);
  return attribute;
}

function tracerMesh(material: THREE.MeshBasicNodeMaterial, tint: THREE.InstancedBufferAttribute): THREE.InstancedMesh {
  const geometry = new THREE.PlaneGeometry(1, 1);
  geometry.setAttribute(TRACER_TINT_ATTRIBUTE, tint);
  const mesh = new THREE.InstancedMesh(geometry, material, MAX_TRACERS);
  mesh.frustumCulled = false;
  mesh.count = 0;
  mesh.visible = false;
  mesh.renderOrder = TRACER_RENDER_ORDER;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  return mesh;
}

const scratchMatrix = new THREE.Matrix4();
const scratchAlong = new THREE.Vector3();
const scratchAcross = new THREE.Vector3();
const scratchNormal = new THREE.Vector3();
const scratchPosition = new THREE.Vector3();
const scratchScale = new THREE.Vector3();

/**
 * The two meshes plus their shared bolt pool, as one thing `combat-visuals.ts`
 * can add to a group and hand a frame's rounds.
 *
 * A class rather than a factory of closures for the reason `SearchlightBeams`
 * is one: it holds mutable buffers and a handful of methods.
 */
export class TracerPool {
  readonly group = new THREE.Group();
  private readonly tailTints = instancedTint();
  private readonly headTints = instancedTint();
  private readonly tails = tracerMesh(tracerTailMaterial(), this.tailTints);
  private readonly heads = tracerMesh(tracerHeadMaterial(), this.headTints);
  private readonly bolts: TracerBolt[] = createTracerPool();
  /** How many bolts `write` last placed — read by the census in `apps/web/test`. */
  live = 0;

  constructor() {
    this.group.add(this.tails, this.heads);
  }

  /**
   * The pool `fillTracerPool` fills. Handed out rather than filled here so the
   * arithmetic stays in the pure module and this class stays scene-graph work.
   */
  boltPool(): TracerBolt[] {
    return this.bolts;
  }

  /**
   * Composes both meshes' instance matrices for the first `count` bolts and
   * hides both when there are none — `visible = false` takes a mesh out of the
   * render list entirely, so an empty sky costs zero draw calls rather than two
   * empty ones, and a scene with the toggle off costs zero rather than two.
   *
   * `orientation` is the camera's own world rotation, which the head quads are
   * billboarded to. The camera must therefore already be placed for this frame,
   * exactly as `writeBullets` requires: reading it afterwards faces the whole
   * burst at last frame's attitude.
   */
  place(count: number, orientation: THREE.Quaternion): void {
    for (let index = 0; index < count; index++) {
      const bolt = this.bolts[index]!;
      scratchAlong.set(bolt.alongX, bolt.alongY, bolt.alongZ);
      scratchAcross.set(bolt.acrossX, bolt.acrossY, bolt.acrossZ);
      scratchNormal.set(bolt.normalX, bolt.normalY, bolt.normalZ);
      // `makeBasis` writes the three axes as the matrix's columns, which *is*
      // the rotation-and-scale, so scaling each unit axis first is how the
      // ribbon gets its length and width without a second multiply.
      scratchMatrix.makeBasis(
        scratchAlong.multiplyScalar(bolt.tailLengthM),
        scratchAcross.multiplyScalar(bolt.tailWidthM),
        scratchNormal,
      );
      scratchMatrix.setPosition(bolt.tailX, bolt.tailY, bolt.tailZ);
      this.tails.setMatrixAt(index, scratchMatrix);

      scratchPosition.set(bolt.headX, bolt.headY, bolt.headZ);
      scratchScale.set(bolt.headSizeM, bolt.headSizeM, 1);
      scratchMatrix.compose(scratchPosition, orientation, scratchScale);
      this.heads.setMatrixAt(index, scratchMatrix);

      // Both quads of one round take the same rung, so the head's halo and the
      // ribbon it meets are the same colour where they meet.
      const tint = TRACER_TIER_TINT[bolt.tier];
      this.tailTints.setXYZ(index, tint[0], tint[1], tint[2]);
      this.headTints.setXYZ(index, tint[0], tint[1], tint[2]);
    }
    for (const mesh of [this.tails, this.heads]) {
      mesh.count = count;
      mesh.instanceMatrix.needsUpdate = true;
      mesh.visible = count > 0;
    }
    this.tailTints.needsUpdate = true;
    this.headTints.needsUpdate = true;
    this.live = count;
  }

  dispose(): void {
    this.tails.geometry.dispose();
    this.heads.geometry.dispose();
    (this.tails.material as THREE.Material).dispose();
    (this.heads.material as THREE.Material).dispose();
    this.group.clear();
  }
}
